import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CloudRain,
  CloudSun,
  Droplets,
  Eye,
  RefreshCw,
  ShieldCheck,
  Sun,
  Wind,
  WifiOff,
  X,
} from "lucide-react";
import {
  fetchPointWeather,
  fetchRouteWeather,
  INAMHI_ALERTS_URL,
  OPEN_METEO_URL,
  type RouteWeather,
  type RouteWeatherPoint,
} from "../lib/route-weather";
import type { RouteCoordinate } from "../lib/navigation-core";
import type { RouteGeoJson } from "../lib/route-workflow";

type WeatherTheme = "sunny" | "rainy" | "cloudy";

function weatherTheme(point?: RouteWeatherPoint): WeatherTheme {
  const code = point?.weatherCode ?? 0;
  if ((point?.precipitationMm ?? 0) > 0.1 || code >= 51) return "rainy";
  if ([2, 3, 45, 48].includes(code)) return "cloudy";
  return "sunny";
}

function WeatherIcon({ point }: { point?: RouteWeatherPoint }) {
  const theme = weatherTheme(point);
  if (theme === "rainy") return <CloudRain aria-hidden="true" />;
  if (theme === "cloudy") return <CloudSun aria-hidden="true" />;
  return <Sun aria-hidden="true" />;
}

function number(value: number | null, suffix = "") {
  return value === null ? "—" : `${Math.round(value)}${suffix}`;
}

function WeatherPointCard({ point }: { point: RouteWeatherPoint }) {
  return (
    <article className="weather-point-card">
      <header>
        <span>
          <strong>{point.name}</strong>
          <small>{point.elevationM === null ? "Altitud no disponible" : `${point.elevationM.toLocaleString("es-EC")} m s. n. m.`}</small>
        </span>
        <WeatherIcon point={point} />
      </header>
      <strong className="weather-temperature">{number(point.temperatureC, " °C")}</strong>
      <div className="weather-point-details">
        <span title="Probabilidad de precipitación"><Droplets />{number(point.precipitationProbability, "%")}</span>
        <span title="Viento"><Wind />{number(point.windKmh, " km/h")}</span>
        <span title="Ráfagas">Ráf. {number(point.gustKmh, " km/h")}</span>
        <span title="Visibilidad"><Eye />{point.visibilityM === null ? "—" : `${(point.visibilityM / 1000).toFixed(1)} km`}</span>
        <span title="Índice ultravioleta"><Sun />UV {number(point.uvIndex)}</span>
        <span title="Lluvia prevista en las próximas seis horas">6 h · {point.next6HoursPrecipitationMm.toFixed(1)} mm</span>
      </div>
    </article>
  );
}

type RouteWeatherPanelProps = {
  routeId: string;
  routeGeojson?: RouteGeoJson;
  point?: RouteCoordinate;
  compact?: boolean;
};

export function RouteWeatherPanel({ routeId, routeGeojson, point, compact = false }: RouteWeatherPanelProps) {
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState<RouteWeather | null>(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      if (point) setWeather(await fetchPointWeather(routeId, point));
      else if (routeGeojson) setWeather(await fetchRouteWeather(routeId, routeGeojson));
      else throw new Error("No existe una ubicación válida para consultar el pronóstico.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Pronóstico no disponible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setWeather(null);
    setError("");
    const request = point
      ? fetchPointWeather(routeId, point)
      : routeGeojson
        ? fetchRouteWeather(routeId, routeGeojson)
        : Promise.reject(new Error("No existe una ubicación válida para consultar el pronóstico."));
    request
      .then((result) => { if (!cancelled) setWeather(result); })
      .catch((reason: unknown) => { if (!cancelled) setError(reason instanceof Error ? reason.message : "Pronóstico no disponible."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [routeId, routeGeojson, point?.latitude, point?.longitude, point?.elevation]);

  const current = weather?.points[0];
  const theme = weatherTheme(current);
  const updated = useMemo(
    () => weather ? new Date(weather.updatedAt).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "short" }) : "",
    [weather],
  );

  return (
    <section className={`route-weather weather-${theme} ${opened ? "is-open" : "is-closed"}${compact ? " weather-compact" : ""}`} aria-labelledby="route-weather-title">
      <div className="weather-background" aria-hidden="true" />
      <div className="weather-content">
        <header className="weather-summary">
          <span className="weather-main-icon"><WeatherIcon point={current} /></span>
          <span className="weather-summary-copy">
            <strong id="route-weather-title">{compact && point ? "Clima en este punto" : "Clima de la ruta"}</strong>
            {loading ? <small>Actualizando pronóstico…</small> : error ? <small>{error}</small> : (
              <small>Ahora · {number(current?.temperatureC ?? null, " °C")} · lluvia {number(current?.precipitationProbability ?? null, "%")}</small>
            )}
          </span>
          <span className="weather-quick-data" aria-label="Resumen meteorológico">
            <small><Wind />{number(current?.windKmh ?? null, " km/h")}</small>
            <small><Sun />UV {number(current?.uvIndex ?? null)}</small>
          </span>
          <button
            type="button"
            className="weather-toggle"
            onClick={() => setOpened((value) => !value)}
            aria-expanded={opened}
            aria-controls="route-weather-details"
          >
            {opened ? <><span>Cerrar</span><X /></> : <><span>Ver pronóstico</span><ChevronDown /></>}
          </button>
        </header>

        <div id="route-weather-details" className="weather-details" aria-hidden={!opened}>
          {weather?.points.length ? (
            <div className="weather-points">
              {weather.points.map((point) => <WeatherPointCard key={point.name} point={point} />)}
            </div>
          ) : !loading && (
            <p className="weather-unavailable"><CloudSun />{error || "No existe todavía un pronóstico guardado para esta ruta."}</p>
          )}

          <div className={`weather-advisories ${weather?.advisories.length ? "has-advisories" : "is-clear"}`}>
            <header>
              {weather?.advisories.length ? <AlertTriangle /> : <ShieldCheck />}
              <strong>Alertas preventivas THERA</strong>
            </header>
            {weather?.advisories.length ? (
              <ul>{weather.advisories.map((advisory) => <li key={advisory}>{advisory}</li>)}</ul>
            ) : <p>Sin alertas preventivas calculadas para las condiciones consultadas.</p>}
            <small>Estas recomendaciones no sustituyen los avisos oficiales ni el criterio del guía.</small>
          </div>

          <footer className="weather-footer">
            <span>
              {weather?.offline ? <><WifiOff />Último pronóstico guardado sin conexión</> : <>Actualizado {updated}</>}
            </span>
            <span className="weather-sources">
              <a href={OPEN_METEO_URL} target="_blank" rel="noreferrer">Datos: Open-Meteo</a>
              <a href={INAMHI_ALERTS_URL} target="_blank" rel="noreferrer">Alertas oficiales INAMHI</a>
              <button type="button" onClick={load} disabled={loading} aria-label="Actualizar pronóstico"><RefreshCw /></button>
            </span>
          </footer>
          <button type="button" className="weather-bottom-close" onClick={() => setOpened(false)}>
            <ChevronUp /> Cerrar pronóstico
          </button>
        </div>
      </div>
    </section>
  );
}
